import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { IconMessageCircleQuestion, IconHomeSearch } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import TableSimple from '@/components/TableSimple';
import {
  useReverseMortgageContactMutation,
  useSendingToTheCustomerForSignatureMutation,
} from '@/services/api';
import { formatNumberWithCommas } from '@/helpers/string';
import {
  Page, ProcessHeader, AmountSlider, DataSummaryCard,
} from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import { REVERSE_MORTGAGE_ACTIONS } from '@/data/constants';
import routes from '@/routes';
import { COMPANIES_ORDER } from '@/helpers/enums';
import { roundNumber } from '@/helpers/number';
import Modal from '@/components/Modal';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';
import { getMutationResult } from '@/helpers/query';
import classes from './reverseMortgage.module.css';

const ReverseMortgage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const reverseMortgageDetails = useSelector((state) => state.reverseMortgage);
  const [showSection, setShowSection] = useState(false);
  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();
  const navigate = useNavigate();
  const borrowersStore = useSelector((state) => state.proposal.borrower);
  const proposal = useSelector((state) => state.proposal);
  const { role } = useSelector((state) => state.user);
  const [opened, { open, close }] = useDisclosure(false);
  const [apiContant] = useReverseMortgageContactMutation();
  const { id, extraDetails } = proposal;
  const { reverseMortgageAction } = extraDetails;
  useIdInStoreGuard();

  // Prepare initial table data for TableSimple
  const companyOrderMap = Object.fromEntries(
    COMPANIES_ORDER.map((c) => [c.name, c.order]),
  );

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      period: reverseMortgageDetails?.period || 40,
      amount: null,
    },
    validate: {},
    validateInputOnChange: true,
  });

  useEffect(() => {
    const hasYearsData = reverseMortgageDetails.companies.some(
      (company) => company.yearsArray.length > 0,
    );
    setShowSection(hasYearsData);
  }, [form.values.period]);

  // Filter and process companies based on reverseMortgageAction
  const getFilteredCompanies = () => {
    const companiesWithData = reverseMortgageDetails?.companies
      .filter((company) => Array.isArray(company.yearsArray)
        && company.yearsArray.length > 0)
      .map((company) => {
        const yearData = company.yearsArray.find(
          // eslint-disable-next-line eqeqeq
          (y) => y.years == form.values.period,
        );
        if (!yearData) return null;
        return {
          company: company.company,
          firstMonthInterest: company.firstMonthInterest,
          monthlyPayment: yearData.monthlyPayment,
          totalPayment: yearData.totalPayment,
        };
      })
      .filter(Boolean);

    if (!companiesWithData || companiesWithData.length === 0) return [];

    // Find the company with the lowest totalPayment
    const lowestTotalPaymentCompany = companiesWithData.reduce((prev, current) => (
      (prev.totalPayment < current.totalPayment) ? prev : current
    ));

    // Return only the company with lowest total payment
    return [lowestTotalPaymentCompany];
  };

  // Prepare table data for TableSimple based on reverseMortgageAction
  const getTableData = () => {
    const filteredCompanies = getFilteredCompanies();

    if (reverseMortgageAction === REVERSE_MORTGAGE_ACTIONS.interestPaymentOnly) {
      return filteredCompanies.flatMap((company) => [
        {
          label: 'החזר חודשי גרייס התחלתי (החזר ריבית בלבד)',
          value: formatNumberWithCommas(roundNumber(company.firstMonthInterest)),
        },
        {
          label: 'סך יתרה לתשלום (קרן בלבד) בסוף התקופה',
          value: formatNumberWithCommas(roundNumber(company.totalPayment)),
        },
      ]);
    }

    if (reverseMortgageAction === REVERSE_MORTGAGE_ACTIONS.paymentOfPrincipalAndInterest) {
      return filteredCompanies.flatMap((company) => [
        {
          label: 'שפיצר החזר חודשי התחלתי (החזר קרן וריבית)',
          value: formatNumberWithCommas(roundNumber(company.monthlyPayment)),
        },
        {
          label: 'סך תשלומים (קרן + ריבית) לכל תקופת ההלוואה',
          value: formatNumberWithCommas(roundNumber(company.totalPayment)),
        },
      ]);
    }

    if (reverseMortgageAction === REVERSE_MORTGAGE_ACTIONS.paymentAtTheEndOfThePeriod) {
      return filteredCompanies.flatMap((company) => [
        {
          label: 'תשלום חודשי',
          value: '0',
        },
        {
          label: 'סך יתרה לתשלום (קרן + ריבית) בסוף התקופה',
          value: formatNumberWithCommas(roundNumber(company.totalPayment)),
        },
      ]);
    }

    // Default fallback - show all columns
    return filteredCompanies.flatMap((company) => [
      {
        label: 'חברה',
        value: company.company,
      },
      {
        label: 'החזר חודשי גרייס התחלתי (החזר ריבית בלבד)',
        value: formatNumberWithCommas(roundNumber(company.firstMonthInterest)),
      },
      {
        label: 'שפיצר החזר חודשי התחלתי (החזר קרן וריבית)',
        value: formatNumberWithCommas(roundNumber(company.monthlyPayment)),
      },
      {
        label: 'סך הכל לתשלום',
        value: formatNumberWithCommas(roundNumber(company.totalPayment)),
      },
    ]);
  };

  const tableData = getTableData();

  // Prepare modal table data for TableSimple
  const modalTableData = {
    head: [
      'חברה',
      'החזר חודשי גרייס התחלתי (החזר ריבית בלבד)',
      'שפיצר החזר חודשי התחלתי (החזר קרן וריבית)',
      'סך הכל תשלומים',
    ],
    body: reverseMortgageDetails?.companies
      .filter((company) => Array.isArray(company.yearsArray)
        && company.yearsArray.some((y) => y.years === Number(form.values.period)))
      .slice()
      .sort(
        (a, b) => (companyOrderMap[a.company] ?? Infinity)
          - (companyOrderMap[b.company] ?? Infinity),
      )
      .map((company) => {
        const yearData = company.yearsArray.find(
          (y) => y.years === Number(form.values.period),
        );
        if (!yearData) return null;
        return [
          company.company,
          formatNumberWithCommas(roundNumber(company.firstMonthInterest)),
          formatNumberWithCommas(roundNumber(yearData.monthlyPayment)),
          formatNumberWithCommas(roundNumber(yearData.totalPayment)),
        ];
      })
      .filter(Boolean),
  };

  const initialTableData = {
    head: [
      'חברה',
      'אחוז מימון לפי טבלת בסיס',
      'סכום הלוואה מקסימאלי בהחזר בלון',
      'סכום הלוואה מקסימאלי בהחזר שפיצר/גרייס',
    ],
    body: reverseMortgageDetails?.companies
      ?.slice()
      .sort(
        (a, b) => (companyOrderMap[a.company] ?? Infinity)
          - (companyOrderMap[b.company] ?? Infinity),
      )
      .map((row) => [
        row.company,
        `${row.percentage}%`,
        formatNumberWithCommas(row.calculatedAmount) || '0',
        formatNumberWithCommas(row.extraLoan) || '0',
      ]),
  };

  const handleSubmit = async () => {
    open();
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'loanAmountReverseMortgage')?.path, { id }));
  };

  const handleSendData = async () => {
    setLoading(true);
    try {
      // Prepare the data to send
      const borrower = borrowersStore[0];
      const selectedCompanies = reverseMortgageDetails?.companies
        .map((company) => {
          const yearData = company.yearsArray?.find(
            (y) => y.years === form.values.period,
          );
          if (!yearData) return null;
          return {
            company: company.company,
            firstMonthInterest: company.firstMonthInterest,
            monthlyPayment: yearData.monthlyPayment,
            totalPayment: yearData.totalPayment,
            period: form.values.period,
          };
        })
        .filter(Boolean);

      const sendValues = {
        period: form.values.period,
        companies: selectedCompanies,
        offerId: id,
        fullName: borrower.fullName,
        phone: borrower.phoneNumber,
        email: borrower.email,
        message: form.values.message || '',
      };

      await getMutationResult(sendValues, apiContant);

      if (role.code !== '5') {
        const sendSummeryLinkObj = {
          phoneNumber: borrower.phoneNumber,
          fullName: borrower.fullName,
          email: borrower.email,
          proposalNumber: id,
        };
        await getMutationResult(sendSummeryLinkObj, apiConfigSendToClientForSignature);
      }

      const successPage = routes.newProposal.pages.find((page) => page.id === 'success');
      navigate(routeWithParams(successPage.path, { id }));
      close();
    } catch (e) {
      // handle error (optional)
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ProcessHeader
        headerText={t('pages.reverseMortgage.title')}
        headerColor="#D4FCF3"
        headerTextColor="#05b8b8"
        showReturnButton
        onReturn={handleReturn}
        icon={IconHomeSearch}
      >

        {showSection && (
          <>
            <Page.Section
              title="בחרו את מספר השנים לפריסת החזר ההלוואה"
              withBackground
            >
              <AmountSlider
                value={form.values.period}
                onChange={(value) => form.setFieldValue('period', value)}
                min={2}
                max={40}
                step={1}
                size="xl"
                showValue={true}
                valueSuffix="שנים"
                marksStep={5}
              />
              <div className={classes.tableContainer}>
                <DataSummaryCard details={tableData} />
                <p>*החישוב בהצעה זו נעשה על בסיס הריבית הממוצעת המקובלת במסלולי ריבית קבועה
                  צמודה למדד בהתאם לשיעורים המקובלים כיום בשוק</p>
                <p>*הסכומים והערכים המוצגים בטבלה הינם בערכים נומינליים ללא מרכיב ההצמדה </p>
                <p>*מתן ההלוואה ותנאיה הסופיים כפופים לתנאים המקובלים בכל חברה
                  בהתאם לנתוניו האישיים של כל לקוח ובמועד נטילת ההלוואה
                </p>
              </div>
            </Page.Section>
            {/* <Page.Section
              withBackground
              title={`המידע שמוצג מתייחס לתקופה של ${form.values?.period} שנים`}
            >
            </Page.Section> */}
          </>
        )}

        <Page.Section
          withBackground
          withCollapse
          title="לקיחת ההלוואה אפשרית בחברות הבאות"
          startClose
        >

          <TableSimple data={initialTableData} />
        </Page.Section>

        <Page.Actions>

          <Button
            variant='filled'
            type='submit'
            loading={loading}
            onClick={form.onSubmit(handleSubmit)}
            rightSection={<IconMessageCircleQuestion size={16} />}
            disabled={!showSection}
          >
            שליחה לייעוץ
          </Button>
        </Page.Actions>
      </ProcessHeader >

      <Modal
        opened={opened}
        close={close}
        title={'פרטי ההצעה עבור הלקוח'}
        showFooterBackground
        size="lg"
      >
        <div>
          {borrowersStore.length > 0 && (
            <Box mb="md" p="md" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <Text size="lg" weight={600} mb="xs">
                {borrowersStore[0].fullName}
              </Text>
              <Text size="sm" mb="xs">
                <strong>טלפון:</strong> {borrowersStore[0].phoneNumber}
              </Text>
              <Text size="sm" mb="xs">
                <strong>אימייל:</strong> {borrowersStore[0].email}
              </Text>
            </Box>
          )}
          <h4>{t('pages.reverseMortgage.selectedPeriod', { period: form.values.period })}</h4>
          <TableSimple data={modalTableData} />
          <TextInput
            mt={'1rem'}
            label="הערות"
            {...form.getInputProps('message')}
          />
          <Button
            mt="md"
            fullWidth
            loading={loading}
            onClick={handleSendData}
          >
            {t('globals.submit')}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ReverseMortgage;
